import { z } from 'zod';
import { Address, DocumentType, DmfStatus } from '@lg/shared';

export { DmfStatus };

export const LegalEntity = z.object({
  id: z.string().min(1).max(4),
  name: z.string(),
  searchName: z.string().nullable(),
  timeZone: z.string().nullable(),
  address: Address.nullable(),
  region: z.string().nullable(),
});
export type LegalEntity = z.infer<typeof LegalEntity>;

export const D365User = z.object({
  userId: z.string().min(1).max(20),
  upn: z.string(),
  name: z.string(),
  enabled: z.boolean(),
});
export type D365User = z.infer<typeof D365User>;

export const SecurityRole = z.object({ roleId: z.string(), name: z.string() });
export type SecurityRole = z.infer<typeof SecurityRole>;

export const Vendor = z.object({ vendorId: z.string(), name: z.string(), companyId: z.string() });
export type Vendor = z.infer<typeof Vendor>;

/** Optional text on a create: omitted fields are not sent to D365. */
const optional = z.string().min(1).optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const Customer = z.object({
  customerAccount: z.string(),
  name: z.string(),
  group: z.string(),
  currency: z.string(),
  companyId: z.string(),
});
export type Customer = z.infer<typeof Customer>;

const partyFields = {
  companyId: z.string().min(1).max(4),
  name: z.string().min(1).max(100),
  group: z.string().min(1).max(10),
  currency: z.string().length(3),
  phone: optional,
  email: optional,
  street: optional,
  city: optional,
  state: optional,
  zip: optional,
  paymentTerms: optional,
};

/**
 * A company's work calendar (the Calendar entity). Workflow due dates use one (24hr), and calendars
 * are company-specific, so a workflow imported before its calendar exists loses the reference.
 * Times are seconds after midnight (86400 = 24:00); workingDays run Monday to Sunday.
 */
export const WorkCalendarSpec = z.object({
  companyId: z.string().min(1).max(4),
  calendarId: z.string().min(1).max(10),
  name: z.string().min(1).max(60),
  workHours: z.number().min(0).max(24),
  startSeconds: z.number().int().min(0).max(86_400),
  endSeconds: z.number().int().min(0).max(86_400),
  workingDays: z.array(z.boolean()).length(7),
});
export type WorkCalendarSpec = z.infer<typeof WorkCalendarSpec>;

// People records created one at a time over OData (Import people, live): each is created unless
// it exists, never changed or removed. Dates are yyyy-mm-dd; the record runs to the open end.
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const companyKey = z.string().min(1).max(4);
const recordKey = z.string().min(1).max(25);

export const EnsureJobInput = z.object({ jobId: recordKey, description: z.string().min(1).max(60) });
export type EnsureJobInput = z.infer<typeof EnsureJobInput>;

/** A worker and their employment in the company. */
export const EnsureWorkerInput = z.object({
  companyId: companyKey,
  personnelNumber: recordKey,
  firstName: z.string().min(1).max(25),
  lastName: z.string().min(1).max(25),
  email: z.string().email(),
  startDate: isoDay,
});
export type EnsureWorkerInput = z.infer<typeof EnsureWorkerInput>;

export const EnsurePositionInput = z.object({
  companyId: companyKey,
  /**
   * Omitted: D365 numbers it from the Position number sequence, and the company's position on
   * this job (paid by the company) counts as already there.
   */
  positionId: recordKey.optional(),
  /**
   * With no ID: a position on this job in any company counts as already there (a shared seat
   * such as the finance director, created once for every venue).
   */
  anyCompany: z.boolean().optional(),
  description: z.string().min(1).max(60),
  jobId: recordKey,
  departmentId: z.string().min(1).max(25).optional(),
  activation: isoDay,
});
export type EnsurePositionInput = z.infer<typeof EnsurePositionInput>;

/** The position's line (managerial) reporting line. */
export const EnsureReportingLineInput = z.object({
  positionId: recordKey,
  parentPositionId: recordKey,
  validFrom: isoDay,
});
export type EnsureReportingLineInput = z.infer<typeof EnsureReportingLineInput>;

export const EnsureAssignmentInput = z.object({
  positionId: recordKey,
  personnelNumber: recordKey,
  primary: z.boolean(),
  validFrom: isoDay,
});
export type EnsureAssignmentInput = z.infer<typeof EnsureAssignmentInput>;

/** A pay cycle (positions need one), created unless it exists. */
export const EnsurePayCycleInput = z.object({
  id: z.string().min(1).max(10),
  description: z.string().min(1).max(60),
});
export type EnsurePayCycleInput = z.infer<typeof EnsurePayCycleInput>;

/** Links a D365 user to a worker's person (the Person field on the user). */
export const EnsurePersonUserInput = z.object({
  userId: z.string().min(1).max(20),
  personnelNumber: recordKey,
  validFrom: isoDay,
});
export type EnsurePersonUserInput = z.infer<typeof EnsurePersonUserInput>;

export interface EnsureResult {
  created: boolean;
  /** Why nothing was created when the existing record differs (e.g. reports to another position) */
  note?: string;
}

export interface EnsurePositionResult extends EnsureResult {
  /** The position's ID, whether found or created (numbered by D365 when none was given) */
  positionId: string;
}

export const CreateCustomerInput = z.object({
  customerAccount: z.string().min(1).max(20),
  ...partyFields,
});
export type CreateCustomerInput = z.infer<typeof CreateCustomerInput>;

export const CreateVendorInput = z.object({
  vendorAccount: z.string().min(1).max(20),
  ...partyFields,
  paymentMethod: optional,
});
export type CreateVendorInput = z.infer<typeof CreateVendorInput>;

/** Group IDs that customers, vendors and projects point at, per company. */
export const MasterDataGroups = z.object({
  customerGroups: z.array(z.string()),
  vendorGroups: z.array(z.string()),
  projectGroups: z.array(z.string()),
});
export type MasterDataGroups = z.infer<typeof MasterDataGroups>;

export const ProjectContract = z.object({
  contractId: z.string(),
  name: z.string(),
  customerAccount: z.string(),
  companyId: z.string(),
});
export type ProjectContract = z.infer<typeof ProjectContract>;

export const CreateProjectContractInput = z.object({
  companyId: z.string().min(1).max(4),
  contractId: z.string().min(1).max(20),
  name: z.string().min(1).max(60),
  customerAccount: z.string().min(1).max(20),
});
export type CreateProjectContractInput = z.infer<typeof CreateProjectContractInput>;

export const D365ProjectType = z.enum(['Internal', 'Cost', 'TimeAndMaterial', 'FixedPrice']);
export type D365ProjectType = z.infer<typeof D365ProjectType>;

export const Project = z.object({
  projectId: z.string(),
  name: z.string(),
  projectType: D365ProjectType,
  projectGroup: z.string(),
  contractId: z.string().nullable(),
  parentProjectId: z.string().nullable(),
  companyId: z.string(),
});
export type Project = z.infer<typeof Project>;

export const CreateProjectInput = z.object({
  companyId: z.string().min(1).max(4),
  projectId: z.string().min(1).max(20),
  name: z.string().min(1).max(60),
  projectType: D365ProjectType,
  projectGroup: z.string().min(1).max(10),
  contractId: optional,
  parentProjectId: optional,
  managerPersonnelNumber: optional,
  controllerPersonnelNumber: optional,
  startDate: isoDate,
  endDate: isoDate.optional(),
});
export type CreateProjectInput = z.infer<typeof CreateProjectInput>;

export const Position = z.object({
  positionId: z.string(),
  title: z.string(),
  jobId: z.string(),
  /** Company the position belongs to; null for regional/corporate positions. */
  companyId: z.string().nullable(),
  departmentId: z.string().nullable(),
  reportsToPositionId: z.string().nullable(),
  holderPersonnelNumber: z.string().nullable(),
  holderName: z.string().nullable(),
  holderUpn: z.string().nullable(),
});
export type Position = z.infer<typeof Position>;

export const Worker = z.object({
  personnelNumber: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  upn: z.string().nullable(),
  companyId: z.string().nullable(),
  employmentStart: z.string(),
});
export type Worker = z.infer<typeof Worker>;

export const ApprovalLimit = z.object({
  companyId: z.string(),
  jobId: z.string(),
  documentType: DocumentType,
  amountUsd: z.number().nonnegative(),
});
export type ApprovalLimit = z.infer<typeof ApprovalLimit>;

export const RoleAssignment = z.object({
  userId: z.string(),
  roleId: z.string(),
  companyId: z.string(),
});
export type RoleAssignment = z.infer<typeof RoleAssignment>;

export const CreateLegalEntityInput = z.object({
  id: z.string().min(1).max(4),
  name: z.string().min(1),
  searchName: z.string().max(20),
  timeZone: z.string(),
  region: z.string().nullable().default(null),
  /** Primary address, written with the company (the Address* fields take on create) */
  address: Address.nullable().default(null),
});
export type CreateLegalEntityInput = z.input<typeof CreateLegalEntityInput>;

/** One venue override written after the copy (spec 11.1 step 5). */
export const OverrideSpec = z.object({
  key: z.string().min(1),
  value: z.union([z.string(), z.number(), z.boolean()]),
});
export type OverrideSpec = z.infer<typeof OverrideSpec>;

export const CreateUserInput = z.object({
  userId: z.string().min(1).max(20),
  upn: z.string(),
  name: z.string(),
  companyId: z.string(),
});
export type CreateUserInput = z.infer<typeof CreateUserInput>;

// ── Custom X++ service contracts (xpp/services/contracts) ─────────────────────

export const CopyProjectInfo = z.object({
  exists: z.boolean(),
  sourceCompany: z.string(),
  entityCount: z.number().int(),
  lastModified: z.string(),
});
export type CopyProjectInfo = z.infer<typeof CopyProjectInfo>;

export const StartCopyRequest = z.object({
  projectName: z.string(),
  destinationCompany: z.string(),
  copyNumberSequences: z.boolean(),
  resetToSmallest: z.boolean(),
});
export type StartCopyRequest = z.infer<typeof StartCopyRequest>;

export const StartCopyResponse = z.object({ executionIds: z.array(z.string()).min(1) });

export const ImportWorkflowRequest = z.object({
  company: z.string(),
  workflowKey: z.string(),
  xmlBlobUrl: z.string().url(),
  activate: z.boolean(),
});
export type ImportWorkflowRequest = z.infer<typeof ImportWorkflowRequest>;

export const ImportWorkflowResponse = z.object({ workflowId: z.string(), active: z.boolean() });

/** Recurring integrations message status (VERIFY the exact values). */
export type RecurringMessageStatus =
  | 'Unknown'
  | 'Enqueued'
  | 'Dequeued'
  | 'Acked'
  | 'Processing'
  | 'Processed'
  | 'ProcessedWithErrors'
  | 'PreProcessingError'
  | 'PostProcessingError';

export const RECURRING_TERMINAL: readonly RecurringMessageStatus[] = [
  'Processed',
  'ProcessedWithErrors',
  'PreProcessingError',
  'PostProcessingError',
];

export interface ImportFromPackageRequest {
  packageUrl: string;
  definitionGroupId: string;
  legalEntityId: string;
}
